import { useEffect, useState } from "react";
import "./users.css";
import Sidebar from "../../components/Sidebar";
import {
  createRolesApi,
  getModulesApi,
  getRolegetPermissionsApisApi,
  getRolesApi,
  inviteListApi,
  inviteSendApi,
  updateInviteApi,
} from "../../services/apiCall";
import CommonLoader from "../../components/CommonLoader";
import { useSelector } from "react-redux";
import Pagination from "../../components/Pagination";

// Sub-components
import UserTable from "./components/UserTable";
import UserModal from "./components/UserModal";
import RoleModal from "./components/RoleModal";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [permissions, setPermissions] = useState([]);

  const [showUserModal, setShowUserModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [invitationLink, setInvitationLink] = useState("");

  // Pagination State
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const businessId = useSelector((state) => state?.user?.businessId);
  const userId = useSelector((state) => state?.user?.userId);

  // Role Form State
  const [roleForm, setRoleForm] = useState({ name: "", description: "" });
  const [permissionRows, setPermissionRows] = useState([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rolesRes, modulesRes, permsRes, usersRes] = await Promise.all([
        getRolesApi(),
        getModulesApi(),
        getRolegetPermissionsApisApi(),
        inviteListApi(),
      ]);

      setRoles(rolesRes?.data?.data || []);
      setModules(modulesRes?.data?.data || []);
      setPermissions(permsRes?.data?.data || []);
      setUsers(usersRes?.data || []);
    } catch (err) {
      console.error(err);
      setFetchError("Failed to load data. Please refresh and try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const totalPages = Math.ceil(users.length / itemsPerPage);
  const displayedUsers = users.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage,
  );

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setInvitationLink(""); // Clear just in case
    setShowUserModal(true);
  };

  const handleUserSubmit = async (formData) => {
    setLoading(true);
    try {
      if (selectedUser) {
        // Update
        const res = await updateInviteApi(selectedUser.id, {
          username: formData.username,
          roleId: formData.roleId,
        });
        if (res?.data) {
          alert("User updated successfully!");
        }
      } else {
        // Create
        const payload = {
          invitedEmail: formData.email.trim(),
          roleId: formData.roleId,
          businessid: businessId,
          invitedBy: userId,
        };
        console.log("payload from add user:::::", payload);
        const res = await inviteSendApi(payload);
        console.log("response from add user:::::", res);
        
        const link = res?.data?.data?.invitation?.invitationLink;
        if (link) {
          setInvitationLink(link);
          // Keep loading state true or handle UI as needed
          return; // Don't close modal yet
        } else {
          alert("User invited successfully!");
        }
      }
      fetchData();
      setShowUserModal(false);
    } catch (err) {
      console.error("Operation failed:", err);
      alert(
        err.response?.data?.message || "Operation failed. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Role Logic
  const addNewRow = () => {
    setPermissionRows([
      ...permissionRows,
      { id: Date.now(), moduleId: null, permissionIds: [] },
    ]);
  };

  const updateRow = (index, updates) => {
    const newRows = [...permissionRows];
    newRows[index] = { ...newRows[index], ...updates };
    setPermissionRows(newRows);
  };

  const removeRow = (index) => {
    setPermissionRows(permissionRows.filter((_, i) => i !== index));
  };

  const handleCreateRole = async () => {
    const rolePermissions = [];
    permissionRows.forEach((row) => {
      if (row.moduleId && row.permissionIds.length > 0) {
        row.permissionIds.forEach((pId) => {
          rolePermissions.push({ moduleId: row.moduleId, permissionId: pId });
        });
      }
    });

    try {
      setLoading(true);
      const code = roleForm.name
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "");
      const res = await createRolesApi({
        name: roleForm.name.trim(),
        description: roleForm.description.trim(),
        code,
        rolePermissions,
      });

      if (res?.data) {
        alert("Role created successfully!");
        fetchData();
        setShowRoleModal(false);
        setRoleForm({ name: "", description: "" });
        setPermissionRows([]);
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to create role");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app">
      <Sidebar />
      <CommonLoader visible={loading} />

      <main className="main-content">
        <div className="page-header">
          <div>
            <h1>Users & Permissions</h1>
            <p className="subtitle">
              Manage users and their roles professionaly
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => {
              setSelectedUser(null);
              setInvitationLink("");
              setShowUserModal(true);
            }}
            disabled={loading}
          >
            + New User
          </button>
        </div>

        {fetchError && (
          <div className="alert-error" style={{ marginBottom: "24px" }}>
            {fetchError}
          </div>
        )}

        <UserTable
          users={displayedUsers}
          roles={roles}
          page={page}
          itemsPerPage={itemsPerPage}
          onEdit={handleEditUser}
        />

        {users.length > 0 && (
          <div style={{ marginTop: "24px" }}>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              totalItems={users.length}
              itemsPerPage={itemsPerPage}
              onPageSizeChange={(newSize) => {
                setItemsPerPage(newSize);
                setPage(1);
              }}
            />
          </div>
        )}

        <UserModal
          isOpen={showUserModal}
          onClose={() => {
            setShowUserModal(false);
            if (invitationLink) {
              setInvitationLink("");
              fetchData();
              setLoading(false); // Ensure loading is off if we were waiting for success
            }
          }}
          user={selectedUser}
          roles={roles}
          onSubmit={handleUserSubmit}
          loading={loading}
          invitationLink={invitationLink}
          onCreateRole={() => {
            setShowUserModal(false);
            setShowRoleModal(true);
          }}
        />

        <RoleModal
          isOpen={showRoleModal}
          onClose={() => setShowRoleModal(false)}
          roleForm={roleForm}
          setRoleForm={setRoleForm}
          permissionRows={permissionRows}
          addNewRow={addNewRow}
          removeRow={removeRow}
          updateRow={updateRow}
          modules={modules}
          permissions={permissions}
          onSave={handleCreateRole}
        />
      </main>
    </div>
  );
};

export default Users;
